// Bulk CSV export of the user base (name, email, signup date, status).
//
// Kept out of AdminUsersTab: that file is already well past the size cap,
// and the options popover is self-contained.
//
// Two knobs, chosen at click time:
//   - "apply on-screen filters" hands the current search/status/2FA
//     selection through to the server
//   - the include-disabled / include-unverified toggles are the coarse
//     alternative when no screen filter is in play
//
// They're mutually exclusive by design: the screen's status dropdown and
// the include-disabled checkbox both write `is_active`, and letting both
// fire produces an empty intersection ("Disabled" + "don't include
// disabled") with no visible explanation. So the toggles grey out while
// the filter passthrough is on.

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { downloadUsersCsv } from "@/hooks/useAdmin";
import type { UsersCsvExportFilters } from "@/hooks/useAdmin";
import { useDict } from "@/i18n/LocaleProvider";
import { toast } from "@/stores/toastStore";

/** The subset of the tab's filter state the export can pass through. */
export interface ScreenFilters {
  q?: string;
  is_active?: string;
  has_2fa?: string;
}

function parseBool(value?: string): boolean | undefined {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

export function UsersCsvExportButton({ screen }: { screen: ScreenFilters }) {
  const t = useDict().app.adminUsers;
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [applyScreenFilters, setApplyScreenFilters] = useState(false);
  const [includeDisabled, setIncludeDisabled] = useState(true);
  const [includeUnverified, setIncludeUnverified] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function buildFilters(): UsersCsvExportFilters {
    if (applyScreenFilters) {
      return {
        q: screen.q || undefined,
        is_active: parseBool(screen.is_active),
        has_2fa: parseBool(screen.has_2fa),
      };
    }
    return {
      is_active: includeDisabled ? undefined : true,
      email_verified: includeUnverified ? undefined : true,
    };
  }

  async function handleExport() {
    setExporting(true);
    try {
      await downloadUsersCsv(buildFilters());
      toast.success(t.csvExported);
      setOpen(false);
    } catch (e) {
      toast.error(
        `${t.csvExportFailed} ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setExporting(false);
    }
  }

  return (
    <div ref={containerRef} className="relative inline-block">
      <Button
        small
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {t.exportCsv} ▾
      </Button>

      {open && (
        <div
          role="dialog"
          aria-label={t.exportCsv}
          className="absolute right-0 z-50 mt-1 w-[280px] rounded animate-fade-in p-3"
          style={{
            background: "rgb(var(--color-bg2))",
            border: "1px solid rgb(var(--color-stroke))",
            boxShadow: "0 12px 32px rgba(0,0,0,0.6)",
          }}
        >
          <div className="font-mono text-[9px] text-muted uppercase tracking-[0.05em] mb-2">
            {t.exportOptions}
          </div>

          <CheckboxRow
            label={t.applyScreenFilters}
            checked={applyScreenFilters}
            onChange={setApplyScreenFilters}
          />
          <CheckboxRow
            label={t.includeDisabled}
            checked={applyScreenFilters ? false : includeDisabled}
            disabled={applyScreenFilters}
            onChange={setIncludeDisabled}
          />
          <CheckboxRow
            label={t.includeUnverified}
            checked={applyScreenFilters ? false : includeUnverified}
            disabled={applyScreenFilters}
            onChange={setIncludeUnverified}
          />

          <div className="font-mono text-[9px] text-dim mt-2 mb-3 leading-[1.5]">
            {t.exportAlwaysExcludes}
          </div>

          <Button
            small
            full
            variant="primary"
            disabled={exporting}
            onClick={handleExport}
          >
            {exporting ? t.exportingCsv : t.downloadCsv}
          </Button>
        </div>
      )}
    </div>
  );
}

function CheckboxRow({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      className={
        "flex items-center gap-2 py-[5px] font-mono text-[11px] " +
        (disabled ? "text-dim cursor-not-allowed" : "text-content cursor-pointer")
      }
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-accent"
      />
      {label}
    </label>
  );
}
